import React, { useState, useEffect } from 'react';
import { Stack, Button, Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import ChevronRightDarkIcon from 'assets/image/icons/chevron_right_dark.svg';
import RefreshIcon from 'assets/image/icons/refresh_black.svg';
import EyeIcon from 'assets/image/icons/eye.svg';
import EyeSlashIcon from 'assets/image/icons/eye_slash.svg';
import PointIcon from 'assets/image/icons/point.svg';
import { getCurrentPoints } from 'api';
import { API_SUCCEEDED } from 'lib/constants';
import AlertMessage from 'component/AlertMessage';
import { errorCodes } from 'lib/errorCodes';
import { formatDate } from 'lib';
import {
  STORAGE_KEYS,
  getStorageBoolean,
  setStorageBoolean
} from 'lib/storageUtils';

// 夜間メンテナンス時間かどうかを判定する
const isMaintenanceTime = () => {
  const now = new Date();
  const hour = now.getHours();
  return hour >= 23 || hour < 7;
};

/**
 * 現在時刻の更新日時文字列を返す
 */
const getUpdateTimeText = () => {
  const now = new Date();
  // yyyy/MM/dd HH:mm:ss 形式でフォーマット
  const formatted = now
    .toLocaleString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    })
    .replace(/\//g, '/');

  return `${formatted} 時点`;
};

const SERVICE_UNAVAILABLE =
  '23:00～7:00の間はメンテナンスを行っています。ポイント残高の確認及びポイントチャージはご利用いただけません。';
const ERROR_MESSAGE =
  '予期せぬエラーが発生しました。繰り返し発生する場合は、店舗サービスカウンターまでお問い合わせください。';

const CardPointBalance: React.FC = () => {
  // ローカルストレージから表示/非表示状態を取得（デフォルトはtrue=表示）
  const [isBalanceVisible, setIsBalanceVisible] = useState<boolean>(true);
  const [updateTime, setUpdateTime] = useState<string>(getUpdateTimeText());
  const [isLoading, setIsLoading] = useState(false);
  const [totalPoint, setTotalPoint] = useState<number | undefined>(undefined);
  const [lapseDueDate, setLapseDueDate] = useState<string | undefined>(
    undefined
  );
  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // 初期表示時にローカルストレージから表示/非表示設定を読み込む
  useEffect(() => {
    setIsBalanceVisible(getStorageBoolean(STORAGE_KEYS.POINT_BALANCE_VISIBLE));
  }, []);

  // ポイント残高を取得する関数
  const fetchPointBalance = async () => {
    // 夜間メンテナンス時間はAPIを呼び出さない
    if (isMaintenanceTime()) {
      setIsError(true);
      setErrorMessage(SERVICE_UNAVAILABLE);
      setTotalPoint(undefined);
      return;
    }

    setIsLoading(true);
    setIsError(false);
    setErrorMessage(null);

    try {
      const res = await getCurrentPoints();

      if (res?.status === API_SUCCEEDED) {
        setTotalPoint(res.data.total_point);
        setLapseDueDate(res.data.lapse_due_date);
      } else if (!res) {
        // レスポンスがない場合は、夜間アクセス制御によるエラーと判断
        setIsError(true);
        setErrorMessage(SERVICE_UNAVAILABLE);
        setTotalPoint(undefined);
        setLapseDueDate(undefined);
      } else {
        // その他のエラー
        setIsError(true);
        setErrorMessage(ERROR_MESSAGE);
        setTotalPoint(undefined);
        setLapseDueDate(undefined);
      }
    } catch (error) {
      console.error('ポイント残高の取得に失敗しました', error);
      setIsError(true);
      setErrorMessage(
        errorCodes['unexpected']?.desc || '予期せぬエラーが発生しました。'
      );
      setTotalPoint(undefined);
      setLapseDueDate(undefined);
    } finally {
      setIsLoading(false);
    }
  };

  // 初回マウント時にポイント残高を取得
  useEffect(() => {
    fetchPointBalance();
  }, []);

  const handleUpdateTime = () => {
    const newTime = getUpdateTimeText();
    setUpdateTime(newTime);
    fetchPointBalance();
  };

  // 表示/非表示を切り替え、ローカルストレージに保存
  const toggleBalanceVisibility = () => {
    const newVisibility = !isBalanceVisible;
    setIsBalanceVisible(newVisibility);
    setStorageBoolean(STORAGE_KEYS.POINT_BALANCE_VISIBLE, newVisibility);
  };

  // 現在がメンテナンス時間かどうか（エラー状態も含む）
  const isDisabled = isMaintenanceTime() || isError;

  return (
    <>
      <Stack
        gap={4}
        className="px-3 py-4 px-4 py-3 mb-3 section-cont section-cont--rounded-2 section-cont--shadow justify-content-between"
      >
        <Stack direction="horizontal" className="w-100 justify-content-between">
          <span className="barcodeValidate">{updateTime}</span>
          <Stack direction="horizontal" gap={3}>
            <Button
              className={`view-btn btn--secondary-light rounded-circle p-2 ${
                isDisabled ? 'disabled' : ''
              }`}
              onClick={toggleBalanceVisibility}
              disabled={isDisabled}
            >
              <img
                src={isBalanceVisible ? EyeIcon : EyeSlashIcon}
                alt="表示切替アイコン"
                width={20}
                height={20}
              />
            </Button>
            <Button
              className={`refresh-btn btn--secondary-light text--dark ${
                isDisabled ? 'disabled' : ''
              }`}
              onClick={handleUpdateTime}
              disabled={isDisabled}
            >
              <img className="me-1" src={RefreshIcon} alt="更新アイコン" />
              更新
            </Button>
          </Stack>
        </Stack>
        {isError && errorMessage && (
          <Stack
            direction="horizontal"
            className="w-100 justify-content-between"
          >
            <AlertMessage type="error" message={errorMessage} />
          </Stack>
        )}
        <Stack direction="horizontal" className="w-100 justify-content-between">
          <span className="d-flex align-items-center">
            <img
              src={PointIcon}
              alt="ポイントアイコン"
              style={{ width: 28, height: 28 }}
              className="me-2"
            />
            <span className="d-flex flex-column fw-bold">
              <span>ポイント</span>
              <span>残高</span>
            </span>
          </span>
          <span className="d-flex flex-column align-items-end">
            <span>
              <span className="fw-bold text--big me-2">
                {isLoading ? (
                  <Spinner animation="border" role="status" />
                ) : isBalanceVisible ? (
                  totalPoint?.toLocaleString() ?? '---'
                ) : (
                  '--,---'
                )}
              </span>
              <span className="fw-bold fs-4 text--secondary-light">P</span>
            </span>
            {isBalanceVisible &&
              totalPoint !== undefined &&
              totalPoint > 0 &&
              lapseDueDate && (
                <div className="d-flex">
                  <span className="text--secondary">有効期限</span>
                  <span className="text--secondary fw-bold ms-2">
                    {formatDate(lapseDueDate, false, false)}
                  </span>
                </div>
              )}
          </span>
        </Stack>
        <Link
          to={isDisabled ? '#' : '/prepaid-charge'}
          className={`btn btn--secondary-outline py-3 ${
            isDisabled ? 'disabled' : ''
          }`}
          onClick={(e) => isDisabled && e.preventDefault()}
        >
          ポイントを電子マネーに交換{' '}
          <img
            src={ChevronRightDarkIcon}
            className="ms-2"
            alt="右矢印アイコン"
          />
        </Link>
      </Stack>
    </>
  );
};

export default CardPointBalance;
